import { DOCUMENT, EnvironmentProviders, inject, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AnalyticsService, PageContext } from './analytics.service';

/**
 * Installs the GA4 tag and starts page-view tracking.
 *
 * Everything here is browser-only. Every non-/api request is server-rendered
 * -- web.config routes them to wwwroot/server/server.mjs through iisnode on
 * all three IIS tiers -- and `gtag` does not exist in that context.
 *
 * The measurement ID deliberately does not live in index.html. index.html has
 * no fileReplacements entry in angular.json, so a hardcoded ID there would
 * ship the production property to dev.kilowant.com and mix developer traffic
 * into real reports. index.html carries only the dataLayer/gtag shim; the ID
 * comes from the environment file and is applied here.
 */
export function provideAnalytics(): EnvironmentProviders {
    return provideAppInitializer(() => {
        if (!isPlatformBrowser(inject(PLATFORM_ID))) { return; }

        const document = inject(DOCUMENT);
        const router = inject(Router);
        const analytics = inject(AnalyticsService);

        if (isOptedOut()) {
            // Belt and braces. Returning early means gtag.js is never even
            // requested, so this flag has nothing left to disable -- but it
            // costs nothing and covers anything that loads the tag later.
            (window as unknown as Record<string, boolean>)[`ga-disable-${environment.GA4_MEASUREMENT_ID}`] = true;
            console.info('[analytics] opted out on this browser');
            return;
        }

        installGtag(document);

        // page_view is sent from here rather than from 25 ngOnInit hooks.
        // ngOnInit fires per component, not per navigation, so it missed
        // route changes that reused a component and double-fired ones that
        // did not -- and it ran during server render, where it threw.
        router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => {
                analytics.trackPageView(resolvePageContext(router, document, event.urlAfterRedirects));
            });
    });
}

/** localStorage key holding the staff opt-out. Value is irrelevant; presence is the signal. */
const OPT_OUT_KEY = 'kw_analytics_opt_out';

/**
 * Staff opt-out for this browser.
 *
 * `?analytics=off` sets it, `?analytics=on` clears it, and the choice then
 * persists with no further action. A link is the whole interface, because it
 * can be pasted into Slack and used by someone who will not open DevTools.
 *
 * This exists because the usual answer -- an internal-traffic IP rule -- does
 * not hold up for a team working from home. Residential IPs rotate, and a
 * stale rule stops matching silently; you find out months later, by which
 * point the data is permanently in the property. The two controls are
 * complementary rather than redundant: an IP rule covers every browser and
 * device on one network, this covers every network from one browser. Neither
 * covers a colleague on a new laptop, so both are worth having.
 *
 * See docs/analytics.md -> Excluding Internal Traffic.
 */
function isOptedOut(): boolean {
    try {
        const value = new URLSearchParams(window.location.search).get('analytics');

        if (value === 'off') { localStorage.setItem(OPT_OUT_KEY, '1'); }
        if (value === 'on') { localStorage.removeItem(OPT_OUT_KEY); }

        return localStorage.getItem(OPT_OUT_KEY) != null;
    } catch {
        // Storage access throws outright in some contexts (blocked site data,
        // hardened privacy modes). Fail open: an uncounted opt-out is a far
        // smaller problem than an initializer that throws on every page load.
        return false;
    }
}

function installGtag(document: Document): void {
    const measurementId = environment.GA4_MEASUREMENT_ID;
    if (!measurementId) { return; }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Defined by the shim in index.html, so it is always callable by now.
    // Anything sent before the script above finishes loading queues in
    // dataLayer and flushes when it does.
    const gtag = (window as unknown as { gtag: (...args: unknown[]) => void }).gtag;

    gtag('js', new Date());
    gtag('config', measurementId, {
        // The router subscription above sends every page_view, including the
        // first. Leaving this on double-counts the landing page. The matching
        // admin change is turning off history-based page views under Enhanced
        // measurement on the data stream -- see docs/analytics.md.
        send_page_view: false,
        debug_mode: environment.GA4_DEBUG,
        // Without this, gtag caches document.location as the default
        // page_location and stamps it on every event, tokens and all. The
        // first NavigationEnd replaces this with the route pattern moments
        // later; this value only has to be safe, not final.
        page_location: window.location.origin + window.location.pathname
            + keepMarketingParams(new URLSearchParams(window.location.search)),
    });
}

/**
 * Query parameters that may be sent to GA4.
 *
 * Allowlist, not denylist, and deliberately so. Kilowant puts credentials in
 * the query string: `?data=` carries the activation, advisor login-setup and
 * password-reset tokens (templates/confirm_email.cshtml and siblings), and
 * `?auth=` carries a standing auth token that app.component.ts:122 exchanges
 * for the user's email through AuthService.checkAuth(). Those must never
 * reach Google. A denylist would cover only the params that exist today,
 * and links already sitting in customers' inboxes cannot be recalled.
 *
 * What survives is the marketing set, because dropping it would break the
 * campaign attribution that is GA4's whole reason for being here.
 */
const MARKETING_PARAMS: ReadonlySet<string> = new Set([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
    'gclid', 'gbraid', 'wbraid', 'dclid', 'msclkid', 'fbclid',
]);

function keepMarketingParams(source: URLSearchParams | Params): string {
    const kept = new URLSearchParams();

    const append = (key: string, value: string) => {
        if (MARKETING_PARAMS.has(key.toLowerCase())) { kept.append(key, value); }
    };

    if (source instanceof URLSearchParams) {
        source.forEach((value, key) => append(key, value));
    } else {
        for (const [key, value] of Object.entries(source)) {
            for (const single of Array.isArray(value) ? value : [value]) {
                if (single != null) { append(key, String(single)); }
            }
        }
    }

    const query = kept.toString();

    return query ? `?${query}` : '';
}

/**
 * The page context for one navigation: a scrubbed location, the route
 * pattern, and the title.
 *
 * `path` is the route *pattern* -- "/view-priceplan/:id", not
 * "/view-priceplan/8471". 32 routes across app.routing.ts and
 * admin.routing.ts take an id, and sending resolved URLs gives GA4 one
 * "Pages and screens" row per record. GA4 caps a dimension at roughly 500
 * distinct values per day in standard reports and buckets the overflow into
 * "(other)" -- which is applied to the whole table, so genuine low-volume
 * rows disappear with the long tail. `routeConfig.path` is the declared
 * pattern and is what the snapshot chain exposes, so the pattern costs
 * nothing to build.
 *
 * The title is the *last* one defined on the way down, matching Angular's
 * own TitleStrategy.buildTitle(). Taking the deepest node's title instead
 * would fall through to document.title for the admin children, which have
 * no title of their own -- and document.title is exactly the value that may
 * still hold the previous route's text, since nothing orders TitleStrategy
 * ahead of this subscriber.
 */
function resolvePageContext(router: Router, document: Document, url: string): PageContext {
    let route: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
    const segments: string[] = [];
    let title: string | undefined;

    while (route) {
        const pattern = route.routeConfig?.path;
        if (pattern) { segments.push(pattern); }
        if (route.title) { title = route.title; }
        route = route.firstChild;
    }

    const path = '/' + segments.join('/');
    const query = keepMarketingParams(router.parseUrl(url).queryParams);

    return {
        // Pattern here too, not the resolved path. GA4 derives Landing page
        // and hostname from page_location rather than page_path, so letting
        // the two disagree would leave ids in half the reports.
        page_location: window.location.origin + path + query,
        page_path: path + query,
        page_title: title ?? document.title,
    };
}
